import React, { useState, useEffect } from 'react';
import { Card, Upload, Button, message, Space, Select, Table, Input, Typography } from 'antd';
import { UploadOutlined, InboxOutlined } from '@ant-design/icons';

const { Dragger } = Upload;
const { Text } = Typography;

const FinishWindowInfoPage = () => {
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [aiExtractionTableColumns, setAiExtractionTableColumns] = useState([]);
  const [aiExtractionRows, setAiExtractionRows] = useState([]);
  const [aiExtractionRawText, setAiExtractionRawText] = useState('');
  const [aiExtractionError, setAiExtractionError] = useState('');
  const [aiExtractionFileName, setAiExtractionFileName] = useState('');

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    setVendorsLoading(true);

    try {
      const response = await fetch('/api/bms/vendors');
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || `업체 목록 조회 실패 (${response.status})`);
      }

      const vendorList = result.data || [];
      setVendors(vendorList);

      if (vendorList.length > 0) {
        setSelectedVendorId(vendorList[0].vendor_id);
      }
    } catch (error) {
      message.error('업체 목록을 불러오지 못했습니다.');
    } finally {
      setVendorsLoading(false);
    }
  };

  const createBatch = async () => {
    const payload = {
      vendor_id: selectedVendorId,
      source_type: 'upload',
      source_ref: 'wintec-hr-web',
      status: 'received',
      note: `웹 업로드 ${new Date().toLocaleString('ko-KR')}`
    };

    const response = await fetch('/api/bms/batches', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    let result = {};
    try {
      result = await response.json();
    } catch {
      result = {};
    }

    if (!response.ok || !result.success) {
      throw new Error(result.message || `배치 생성 실패 (${response.status})`);
    }

    const generatedBatchId = result.data?.batch_id ?? result.batch_id ?? result.id;

    if (!generatedBatchId) {
      throw new Error('배치 생성 응답에서 batch_id를 찾지 못했습니다.');
    }

    return generatedBatchId;
  };

  const handleUpload = async () => {
    if (!selectedVendorId) {
      message.warning('업체를 먼저 선택해주세요.');
      return;
    }

    if (fileList.length === 0) {
      message.warning('업로드할 파일을 선택해주세요.');
      return;
    }

    let batchId;
    try {
      batchId = await createBatch();
      setCurrentBatchId(batchId);
    } catch (error) {
      message.error(`배치 생성 실패: ${error.message}`);
      return;
    }

    setUploading(true);
    setAiExtractionTableColumns([]);
    setAiExtractionRows([]);
    setAiExtractionRawText('');
    setAiExtractionError('');
    setAiExtractionFileName('');

    for (const file of fileList) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('batch_id', batchId);

        const response = await fetch('/api/bms/assets/upload', {
          method: 'POST',
          body: formData,
        });

        const result = await response.json();
        const aiExtraction = result?.ai_extraction;

        console.group(`📦 업로드 응답 디버그: ${file.name}`);
        console.log('HTTP 상태', {
          status: response.status,
          ok: response.ok,
        });
        console.log('응답 전체(result)', result);
        console.log('AI 추출(ai_extraction)', aiExtraction);
        console.groupEnd();

        if (aiExtraction) {
          setAiExtractionFileName(file.name);
          applyAiExtractionToTable(aiExtraction);
        } else {
          console.warn(`⚠️ ai_extraction 필드 없음: ${file.name}`);
        }

        if (result.success) {
          message.success(`${file.name} 업로드 성공`);
        } else {
          message.error(`${file.name} 업로드 실패: ${result.message || result.error}`);
        }
      } catch (error) {
        console.error(`❌ 업로드 예외: ${file.name}`, error);
        message.error(`${file.name} 업로드 중 오류 발생`);
      }
    }

    setFileList([]);
    setUploading(false);
  };

  const applyAiExtractionToTable = (aiExtraction) => {
    if (!aiExtraction.success) {
      console.group('❌ AI 추출 실패 상세');
      console.error('ai_extraction.error', aiExtraction.error);
      console.log('ai_extraction.raw_text', aiExtraction.raw_text);
      console.log('ai_extraction 원본', aiExtraction);
      console.groupEnd();

      setAiExtractionTableColumns([]);
      setAiExtractionRows([]);
      setAiExtractionRawText(aiExtraction.raw_text || '');
      setAiExtractionError(aiExtraction.error || 'AI 추출에 실패했습니다.');
      return;
    }

    const extractionData = aiExtraction?.data;
    const { tableColumns, tableRows, fallbackText } = convertExtractionDataToTable(extractionData);

    console.log('✅ AI 추출 성공', {
      dataKeys: extractionData && typeof extractionData === 'object' ? Object.keys(extractionData) : [],
      tableColumns,
      rowsCount: tableRows.length,
      aiExtraction,
    });

    const normalizedRows = normalizeRows(tableRows, tableColumns);
    const normalizedColumns = buildEditableColumns(tableColumns, normalizedRows);

    setAiExtractionTableColumns(normalizedColumns);
    setAiExtractionRows(normalizedRows);
    setAiExtractionRawText(fallbackText);
    setAiExtractionError(
      normalizedRows.length === 0
        ? 'AI 추출은 성공했지만 테이블로 표시할 행 데이터가 없습니다.'
        : ''
    );
  };

  const convertExtractionDataToTable = (data) => {
    if (!data) {
      return { tableColumns: [], tableRows: [], fallbackText: '' };
    }

    const columnsFromData = Array.isArray(data?.columns) ? data.columns : [];
    const rowsFromData = Array.isArray(data?.rows) ? data.rows : [];

    if (columnsFromData.length > 0 || rowsFromData.length > 0) {
      return {
        tableColumns: columnsFromData,
        tableRows: rowsFromData,
        fallbackText: rowsFromData.length === 0 ? JSON.stringify(data, null, 2) : '',
      };
    }

    if (Array.isArray(data)) {
      return {
        tableColumns: [],
        tableRows: data,
        fallbackText: data.length === 0 ? JSON.stringify(data, null, 2) : '',
      };
    }

    if (Array.isArray(data?.items)) {
      return {
        tableColumns: [],
        tableRows: data.items,
        fallbackText: data.items.length === 0 ? JSON.stringify(data, null, 2) : '',
      };
    }

    if (typeof data === 'object') {
      const objectRows = Object.entries(data).map(([field, value], index) => ({
        key: index,
        field,
        value: typeof value === 'object' ? JSON.stringify(value) : value,
      }));

      return {
        tableColumns: ['field', 'value'],
        tableRows: objectRows,
        fallbackText: JSON.stringify(data, null, 2),
      };
    }

    return {
      tableColumns: ['value'],
      tableRows: [{ key: 0, value: String(data) }],
      fallbackText: String(data),
    };
  };

  const normalizeRows = (rows, columns) => {
    if (!Array.isArray(rows)) {
      return [];
    }

    if (rows.length === 0) {
      return [];
    }

    if (Array.isArray(rows[0])) {
      return rows.map((row, rowIndex) => {
        const normalizedRow = { key: rowIndex };
        row.forEach((cellValue, cellIndex) => {
          const fallbackColumnName = `column_${cellIndex + 1}`;
          const configuredColumn = columns[cellIndex];
          const columnName =
            (typeof configuredColumn === 'string' && configuredColumn) ||
            configuredColumn?.key ||
            configuredColumn?.dataIndex ||
            configuredColumn?.name ||
            fallbackColumnName;

          normalizedRow[columnName] = cellValue;
        });
        return normalizedRow;
      });
    }

    return rows.map((row, rowIndex) => ({
      key: row?.key ?? rowIndex,
      ...row,
    }));
  };

  const buildEditableColumns = (columns, rows) => {
    const sourceColumns =
      Array.isArray(columns) && columns.length > 0
        ? columns
        : rows.length > 0
          ? Object.keys(rows[0]).filter((columnName) => columnName !== 'key')
          : [];

    return sourceColumns.map((column, columnIndex) => {
      const dataIndex =
        (typeof column === 'string' && column) ||
        column?.dataIndex ||
        column?.key ||
        column?.name ||
        `column_${columnIndex + 1}`;

      const title =
        (typeof column === 'string' && column) ||
        column?.title ||
        column?.label ||
        dataIndex;

      return {
        title,
        dataIndex,
        key: dataIndex,
        render: (value, record) => (
          <Input
            value={value ?? ''}
            onChange={(event) => handleCellChange(record.key, dataIndex, event.target.value)}
          />
        ),
      };
    });
  };

  const handleCellChange = (rowKey, columnKey, value) => {
    setAiExtractionRows((prevRows) =>
      prevRows.map((row) => (row.key === rowKey ? { ...row, [columnKey]: value } : row))
    );
  };

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      setFileList(prev => [...prev, file]);
      return false;
    },
    fileList,
    multiple: true,
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title="완성창 정보 입력"
        extra={
          <Space>
            <span>업체:</span>
            <Select
              style={{ width: 220 }}
              placeholder="업체 선택"
              value={selectedVendorId}
              onChange={setSelectedVendorId}
              loading={vendorsLoading}
              options={vendors.map(vendor => ({
                label: `${vendor.vendor_name} (#${vendor.vendor_id})`,
                value: vendor.vendor_id
              }))}
              showSearch
              optionFilterProp="label"
            />
            <span>현재 배치 ID:</span>
            <strong>{currentBatchId || '-'}</strong>
          </Space>
        }
      >
        <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">파일을 드래그하거나 클릭하여 업로드</p>
          <p className="ant-upload-hint">
            엑셀, CSV, PDF, 이미지 등 다양한 형식 지원 (최대 100MB)
          </p>
        </Dragger>

        <Space>
          <Button
            type="primary"
            onClick={handleUpload}
            disabled={fileList.length === 0}
            icon={<UploadOutlined />}
            loading={uploading}
          >
            업로드 시작
          </Button>
        </Space>
      </Card>

      {(aiExtractionRows.length > 0 || aiExtractionError || aiExtractionRawText) && (
        <Card title="AI 추출 결과" style={{ marginTop: 24 }}>
          {aiExtractionFileName && (
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">대상 파일: {aiExtractionFileName}</Text>
            </div>
          )}

          {aiExtractionRows.length > 0 && aiExtractionTableColumns.length > 0 && (
            <Table
              columns={aiExtractionTableColumns}
              dataSource={aiExtractionRows}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              scroll={{ x: 'max-content' }}
              bordered
              size="small"
            />
          )}

          {aiExtractionError && (
            <div style={{ marginTop: aiExtractionRows.length > 0 ? 12 : 0 }}>
              <Text type="danger">{aiExtractionError}</Text>
            </div>
          )}

          {aiExtractionRawText && (
            <pre style={{ marginTop: 12, whiteSpace: 'pre-wrap' }}>{aiExtractionRawText}</pre>
          )}
        </Card>
      )}
    </div>
  );
};

export default FinishWindowInfoPage;
